import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../stores/auth";

const navLinks = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/help", label: "Help" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const signOut = useAuthStore((s) => s.signOut);

  const isPortal = location.pathname === "/portal" || location.pathname.startsWith("/portal/");

  async function handleSignOut() {
    await signOut();
    setMobileOpen(false);
    navigate("/");
  }

  return (
    <nav aria-label="Main navigation" className="sticky top-0 z-50 border-b border-forest-800/10 bg-cream-50/95 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src="/logo-dark.svg" alt="Bookburrow" className="h-10" />
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-8 md:flex">
          {!isPortal &&
            navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                aria-current={location.pathname === link.to ? "page" : undefined}
                className={`text-sm font-medium transition-colors hover:text-forest-700 ${
                  location.pathname === link.to
                    ? "text-forest-800 underline decoration-gold-500 decoration-2 underline-offset-8"
                    : "text-bark-600"
                }`}
              >
                {link.label}
              </Link>
            ))}

          {session ? (
            <div className="flex items-center gap-3">
              <Link
                to="/portal/dashboard"
                className="rounded-lg bg-forest-800 px-4 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-forest-700"
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded-lg border border-forest-800/20 px-4 py-2 text-sm font-semibold text-forest-800 transition-all hover:border-forest-800/40 hover:bg-forest-50"
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to="/portal/login"
                className="rounded-lg border border-forest-800/20 px-4 py-2 text-sm font-semibold text-forest-800 transition-all hover:border-forest-800/40 hover:bg-forest-50"
              >
                Sign in
              </Link>
              <a
                href="/#waitlist"
                className="rounded-lg bg-gold-500 px-4 py-2 text-sm font-semibold text-bark-900 shadow-sm transition-all hover:bg-gold-400 hover:shadow-md"
              >
                Join waitlist
              </a>
            </div>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-bark-700 transition-colors hover:bg-cream-200 md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
        >
          {mobileOpen ? (
            <XMarkIcon className="h-5 w-5" />
          ) : (
            <Bars3Icon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div id="mobile-navigation" className="border-t border-cream-300/60 bg-cream-50 px-6 py-4 md:hidden">
          <div className="flex flex-col gap-3">
            {!isPortal &&
              navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={location.pathname === link.to ? "page" : undefined}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-bark-700 transition-colors hover:bg-cream-200"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}

            {session ? (
              <>
                <Link
                  to="/portal/dashboard"
                  className="mt-1 rounded-lg bg-forest-800 px-4 py-2.5 text-center text-sm font-medium text-cream-50 transition-colors hover:bg-forest-700"
                  onClick={() => setMobileOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="rounded-lg border border-forest-800/20 px-4 py-2.5 text-center text-sm font-semibold text-forest-800 transition-colors hover:bg-forest-50"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/portal/login"
                  className="rounded-lg border border-forest-800/20 px-4 py-2.5 text-center text-sm font-semibold text-forest-800 transition-colors hover:bg-forest-50"
                  onClick={() => setMobileOpen(false)}
                >
                  Sign in
                </Link>
                <a
                  href="/#waitlist"
                  className="mt-1 rounded-lg bg-gold-500 px-4 py-2.5 text-center text-sm font-semibold text-bark-900 shadow-sm"
                  onClick={() => setMobileOpen(false)}
                >
                  Join waitlist
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
